import { logger } from '@/helpers/logger';
import { Fragment } from 'react';
import { PageContentItem } from '../../models/page';
import DevErrorMessage from '../DevErrorMessage/DevErrorMessage';
import {
  CONTENTS_AS_PROP_COMPONENT_WHITELIST,
  IGNORED_COMPONENT_NAMES,
} from './consts';
import type { ItemToComponentProps } from './interfaces';

const ItemToComponent = ({ item, cmsComponents }: ItemToComponentProps) => {
  if (!cmsComponents) {
    throw new Error('invalid cms components');
  }

  if (isIgnoredComponent(item)) {
    return <Fragment />;
  }

  const isContents = isContentsComponent(item.componentName);

  // If this is NOT a contents-... wrapper and the component isn't provided
  // in `cmsComponents` then we can't render it.
  if (!isContents && !(item.componentName in cmsComponents)) {
    logger.error(`No component with name: ${item.componentName}`);
    return (
      <DevErrorMessage>
        No component with name: {item.componentName}
      </DevErrorMessage>
    );
  }

  const Component = isContents ? Fragment : cmsComponents[item.componentName];

  const { props, children, shouldIterateChildren } = processItemProps(
    item,
    cmsComponents
  );

  const cleanProps =
    Component === Fragment || !props
      ? undefined
      : Object.fromEntries(
          Object.entries(props).filter(([key]) => !key.startsWith('jcr:'))
        );

  return (
    <Component {...cleanProps}>
      {shouldIterateChildren && Array.isArray(children)
        ? children.map((child) => (
            <ItemToComponent
              key={child.key || child.id}
              item={child}
              cmsComponents={cmsComponents}
            />
          ))
        : children}
    </Component>
  );
};

export default ItemToComponent;

function isIgnoredComponent(item: PageContentItem<any, any, any>) {
  return IGNORED_COMPONENT_NAMES.includes(item.componentName);
}

function isContentsComponent(componentName: string): boolean {
  return componentName.startsWith('contents-');
}

function isWhitelistedForContents(componentName: string): boolean {
  return CONTENTS_AS_PROP_COMPONENT_WHITELIST.some((value) =>
    typeof value === 'string'
      ? value === componentName
      : value.test(componentName)
  );
}

function partitionContents(
  contents: readonly PageContentItem[],
  cmsComponents: Record<string, React.ComponentType<any>>
) {
  const contentsComponents: Record<string, React.ReactNode> = {};
  const regularChildren: PageContentItem[] = [];

  contents.forEach((item) => {
    if (isContentsComponent(item.componentName)) {
      contentsComponents[item.componentName] = (
        <ItemToComponent item={item} cmsComponents={cmsComponents} />
      );
    } else {
      regularChildren.push(item);
    }
  });

  return { contentsComponents, regularChildren };
}

function processItemProps(
  item: PageContentItem,
  cmsComponents: Record<string, React.ComponentType<any>>
) {
  const baseProps = { ...item.props };

  if (!item.contents?.length) {
    return {
      props: baseProps,
      children: baseProps.children,
      shouldIterateChildren: false,
    };
  }

  const { contentsComponents, regularChildren } = partitionContents(
    item.contents,
    cmsComponents
  );

  if (baseProps.children) {
    logger.warn(
      `Component ${item.componentName} has contents AND children. Something is wrong!`
    );
  }

  const processedProps = { ...baseProps, ...contentsComponents };

  if (isWhitelistedForContents(item.componentName)) {
    return {
      props: { ...processedProps, contents: item.contents },
      children: regularChildren,
      shouldIterateChildren: false,
    };
  }

  return {
    props: processedProps,
    children: regularChildren,
    shouldIterateChildren: true,
  };
}
